/**
 * SupabaseAuthProvider - implements AuthProviderAdapter over the Supabase JS client.
 *
 * Wraps supabase.auth.* calls and maps Supabase User/Session → Nuvra AuthUser/AuthSession.
 * Register with authEngine.setProvider(new SupabaseAuthProvider()) at app init.
 *
 * Live Supabase env vars required:
 *   VITE_SUPABASE_URL  + VITE_SUPABASE_ANON_KEY  (browser)
 *   SUPABASE_URL       + SUPABASE_ANON_KEY        (server)
 *
 * Without env vars the Supabase client falls back to localhost stubs (see src/lib/supabase.ts)
 * and all auth calls will fail gracefully - they will not crash the app.
 */

import type { User, Session } from "@supabase/supabase-js";
import { getSupabase }        from "@/lib/supabase";
import { authRedirectUrl } from "@/shell/authRedirect";
import type {
  AuthProviderAdapter,
  AuthCredentials,
  AuthResult,
  AuthUser,
  AuthSession,
  DeviceInfo,
} from "./authEngine";

// ─── Mapping helpers (exported for initSupabaseAuth) ─────────────────────────

export function mapUser(user: User): AuthUser {
  return {
    id:             user.id,
    email:          user.email ?? "",
    displayName:    user.user_metadata?.full_name as string | undefined,
    avatarUrl:      user.user_metadata?.avatar_url as string | undefined,
    emailVerified:  !!user.email_confirmed_at,
    createdAt:      user.created_at ? new Date(user.created_at).getTime() : Date.now(),
    lastLoginAt:    user.last_sign_in_at ? new Date(user.last_sign_in_at).getTime() : Date.now(),
    metadata:       user.user_metadata ?? {},
  };
}

export function mapSession(session: Session): AuthSession {
  const device: DeviceInfo = {
    id:          `supabase-${session.user.id}`,
    userAgent:   typeof navigator !== "undefined" ? navigator.userAgent : "server",
    platform:    typeof navigator !== "undefined" ? navigator.platform  : "server",
    isTrusted:   true,
    firstSeenAt: Date.now(),
  };

  return {
    id:              session.access_token.slice(-16), // stable suffix as session id
    userId:          session.user.id,
    accessToken:     session.access_token,
    refreshToken:    session.refresh_token ?? "",
    expiresAt:       (session.expires_at ?? 0) * 1000,
    device,
    createdAt:       Date.now(),
    lastActivityAt:  Date.now(),
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export class SupabaseAuthProvider implements AuthProviderAdapter {
  readonly id = "email" as const;

  async signUp(credentials: AuthCredentials): Promise<AuthResult> {
    if (!credentials.email || !credentials.password) {
      return { success: false, error: "Email and password required" };
    }

    const { data, error } = await getSupabase().auth.signUp({
      email:    credentials.email,
      password: credentials.password,
    });

    if (error) return { success: false, error: error.message };
    if (!data.user) return { success: false, error: "Sign up returned no user" };

    const user    = mapUser(data.user);
    const session = data.session ? mapSession(data.session) : undefined;

    return {
      success:              true,
      user,
      session,
      requiresVerification: !data.user.email_confirmed_at,
    };
  }

  async signIn(credentials: AuthCredentials): Promise<AuthResult> {
    if (!credentials.email || !credentials.password) {
      return { success: false, error: "Email and password required" };
    }

    const { data, error } = await getSupabase().auth.signInWithPassword({
      email:    credentials.email,
      password: credentials.password,
    });

    if (error) return { success: false, error: error.message };
    if (!data.user || !data.session) return { success: false, error: "Sign in returned incomplete data" };

    return {
      success: true,
      user:    mapUser(data.user),
      session: mapSession(data.session),
    };
  }

  async signOut(_sessionId: string): Promise<void> {
    await getSupabase().auth.signOut();
  }

  async refreshSession(_refreshToken: string): Promise<AuthResult> {
    const { data, error } = await getSupabase().auth.getSession();

    if (error || !data.session) {
      return { success: false, error: error?.message ?? "No active session" };
    }

    return {
      success: true,
      user:    mapUser(data.session.user),
      session: mapSession(data.session),
    };
  }

  async resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await getSupabase().auth.resetPasswordForEmail(email, {
      // "/reset-password" is the route that exists (App.tsx) and the route the
      // live Forgot Password screen already sends people to. "/auth/reset" was
      // never registered, so a recovery link built here landed on nothing: the
      // user clicks the link in a real password-reset email and is stranded on a
      // not-found page with a one-time token already spent.
      redirectTo: authRedirectUrl("/reset-password"),
    });
    return error ? { success: false, error: error.message } : { success: true };
  }

  async verifyEmail(token: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await getSupabase().auth.verifyOtp({
      type:       "email",
      token_hash: token,
    });
    return error ? { success: false, error: error.message } : { success: true };
  }

  async getUser(_userId: string): Promise<AuthUser | null> {
    const { data, error } = await getSupabase().auth.getUser();
    if (error || !data.user) return null;
    return mapUser(data.user);
  }
}
